import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, Users, Film, CheckCircle, Clock, ArrowRight, AlertCircle, Upload, Zap, Activity, ChevronRight, Sparkles, ClipboardList, Check, Circle } from 'lucide-react'
import DashboardLayout from '../components/DashboardLayout'
import FreeExpiryBanner from '../components/FreeExpiryBanner'
import SeasonAssistant from '../components/SeasonAssistant'
import SeasonClubOverview from '../components/SeasonClubOverview'
import ClubLogoOrName from '../components/ClubLogoOrName'
import { useAuth } from '../context/AuthContext'
import { estMembreDeClub, estVraiFree } from '../utils/planAccess'
import { useSeason, formatSeasonShort } from '../context/SeasonContext'
import matchService from '../services/matchService'
import playerService from '../services/playerService'
import api from '../services/api'
import { T } from '../theme'
import { hasScore, scoreDisplay, resultLabel, isWin, isDefeat } from '../utils/matchResult'

// ── Composants atomiques ──────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = {
    DRAFT:      { label: 'Carnet',     color: T.muted,  bg: T.bgAlt,   bdr: T.rule      },
    COMPLETED:  { label: 'Terminé',    color: T.green,  bg: T.greenBg, bdr: T.greenBdr  },
    ANALYZED:   { label: 'Analysé',    color: T.green,  bg: T.greenBg, bdr: T.greenBdr  },
    PROCESSING: { label: 'En cours',   color: T.blue,   bg: T.blueBg,  bdr: T.blueBdr   },
    PENDING:    { label: 'En attente', color: T.muted,  bg: T.bgAlt,   bdr: T.rule      },
    ERROR:      { label: 'Erreur',     color: T.red,    bg: T.redBg,   bdr: T.redBdr    },
    completed:  { label: 'Terminé',    color: T.green,  bg: T.greenBg, bdr: T.greenBdr  },
    processing: { label: 'En cours',   color: T.blue,   bg: T.blueBg,  bdr: T.blueBdr   },
    pending:    { label: 'En attente', color: T.muted,  bg: T.bgAlt,   bdr: T.rule      },
    error:      { label: 'Erreur',     color: T.red,    bg: T.redBg,   bdr: T.redBdr    },
  }[status]
  if (!cfg) return null
  return (
    <span style={{
      fontFamily: T.mono, fontSize: 8, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase',
      color: cfg.color, border: `1px solid ${cfg.bdr}`,
      background: cfg.bg, padding: '3px 8px', flexShrink: 0,
      borderRadius: 20,
    }}>
      {cfg.label}
    </span>
  )
}

const STAT_ACCENTS = [T.accentStat1, T.accentStat2, T.accentStat3, T.accentStat4]

function KpiCard({ label, value, sub, accentIdx = 0, delay = 0 }) {
  const [vis, setVis] = useState(false)
  useEffect(() => { const t = setTimeout(() => setVis(true), delay); return () => clearTimeout(t) }, [delay])
  return (
    <div style={{
      background: T.surface,
      border: `0.5px solid ${T.rule}`,
      borderTop: `2.5px solid ${STAT_ACCENTS[accentIdx] || T.accentStat1}`,
      padding: '18px 20px',
      opacity: vis ? 1 : 0,
      transform: vis ? 'translateY(0)' : 'translateY(12px)',
      transition: 'opacity .35s ease, transform .35s ease',
    }}>
      <p style={{ fontFamily: T.mono, fontSize: 8, fontWeight: 600, letterSpacing: '.12em', textTransform: 'uppercase', color: T.muted2, marginBottom: 10 }}>{label}</p>
      <p style={{ fontFamily: T.display, fontSize: 36, fontWeight: 800, lineHeight: 1, color: T.ink }}>{value}</p>
      {sub && <p style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, marginTop: 6 }}>{sub}</p>}
    </div>
  )
}

function SectionHeader({ title, to, label = 'Voir tout' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: `0.5px solid ${T.rule}` }}>
      <h2 style={{ fontFamily: T.display, fontSize: 18, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: T.ink }}>{title}</h2>
      {to && (
        <Link to={to} style={{ fontFamily: T.mono, fontSize: 10, fontWeight: 500, color: T.muted, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4, transition: 'color .12s' }}
          onMouseEnter={e => e.currentTarget.style.color = T.ink}
          onMouseLeave={e => e.currentTarget.style.color = T.muted}
        >
          {label} <ArrowRight size={10} />
        </Link>
      )}
    </div>
  )
}

function normStats(s) {
  if (!s) return {}
  const val = (v) => {
    if (v === null || v === undefined) return null
    if (typeof v === 'object' && v !== null && 'home' in v) return v.home
    return typeof v === 'number' ? v : null
  }
  return {
    possession:    val(s.possession),
    passAccuracy:  val(s.passAccuracy  ?? s.pass_accuracy),
    shotsOnTarget: val(s.shotsOnTarget ?? s.shots_on_target),
  }
}

function Sparkline({ values, color, height = 32, width = 80 }) {
  if (!values || values.length < 2) return null
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1
  const pad = 3
  const pts = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (width - pad * 2)
    const y = pad + (1 - (v - min) / range) * (height - pad * 2)
    return `${x.toFixed(1)},${y.toFixed(1)}`
  })
  const [lx, ly] = pts[pts.length - 1].split(',')
  return (
    <svg width={width} height={height} style={{ display: 'block', overflow: 'visible', flexShrink: 0 }}>
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" opacity=".9" />
      <circle cx={lx} cy={ly} r="2.5" fill={color} />
    </svg>
  )
}

function EvoCard({ label, values, unit = '', decimals = 0, delay = 0 }) {
  const [vis, setVis] = useState(false)
  useEffect(() => { const t = setTimeout(() => setVis(true), delay); return () => clearTimeout(t) }, [delay])
  const clean = (values || []).filter(v => v !== null && v !== undefined)
  if (clean.length < 2) return null
  const first = clean[0]
  const last  = clean[clean.length - 1]
  const diff  = last - first
  const isUp  = diff > 0.01
  const isDown = diff < -0.01
  const trendColor = isDown ? T.red : isUp ? T.green : T.muted
  const sparkColor = isDown ? T.red : isUp ? T.green : T.muted
  const trendLabel = isDown ? `↓ ${Math.abs(diff).toFixed(decimals)}${unit}` : isUp ? `↑ +${diff.toFixed(decimals)}${unit}` : '— Stable'
  return (
    <div style={{
      background: T.surface, border: `0.5px solid ${T.rule}`, padding: '16px 18px',
      opacity: vis ? 1 : 0, transform: vis ? 'translateY(0)' : 'translateY(10px)',
      transition: 'opacity .35s ease, transform .35s ease',
    }}>
      <p style={{ fontFamily: T.mono, fontSize: 8, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: T.muted2, marginBottom: 10 }}>{label}</p>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <p style={{ fontFamily: T.display, fontSize: 28, fontWeight: 800, lineHeight: 1, color: T.ink }}>{last.toFixed(decimals)}{unit}</p>
          <p style={{ fontFamily: T.mono, fontSize: 10, fontWeight: 500, color: trendColor, marginTop: 5 }}>
            {trendLabel} <span style={{ color: T.muted }}>vs 1er match</span>
          </p>
        </div>
        <Sparkline values={clean} color={sparkColor} />
      </div>
    </div>
  )
}

// ── Checklist de démarrage ────────────────────────────────────

function OnboardingChecklist({ steps }) {
  const [visible, setVisible] = useState(false)
  useEffect(() => { const t = setTimeout(() => setVisible(true), 200); return () => clearTimeout(t) }, [])

  const completed = steps.filter(s => s.done).length
  const total = steps.length
  const progress = (completed / total) * 100

  return (
    <div style={{
      background: T.surface,
      borderLeft: `3px solid ${T.gold}`,
      borderBottom: `1px solid ${T.rule}`,
      padding: '20px 24px',
      marginBottom: 24,
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(-8px)',
      transition: 'opacity .3s ease, transform .3s ease',
    }}>
      {/* En-tête : titre + progression */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Zap size={14} color={T.gold} />
          <div>
            <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.18em', textTransform: 'uppercase', color: 'rgba(79,142,247,0.9)', marginBottom: 2 }}>
              Démarrer avec Insightball
            </div>
            <div style={{ fontFamily: T.display, fontSize: 16, letterSpacing: '.04em', textTransform: 'uppercase', color: T.ink }}>
              {completed} / {total} étape{total > 1 ? 's' : ''} complétée{completed > 1 ? 's' : ''}
            </div>
          </div>
        </div>
        <div style={{ flex: '0 0 auto', minWidth: 100, maxWidth: 160, width: '100%' }}>
          <div style={{ height: 3, background: T.rule, position: 'relative' }}>
            <div style={{ position: 'absolute', inset: 0, width: `${progress}%`, background: T.gold, transition: 'width .4s ease' }} />
          </div>
        </div>
      </div>

      {/* Liste des étapes */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {steps.map((step, i) => {
          const Wrapper = step.done || !step.to ? 'div' : Link
          const wrapperProps = step.done || !step.to ? {} : { to: step.to, state: step.state }
          return (
            <Wrapper
              key={step.key}
              {...wrapperProps}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 0',
                borderTop: i === 0 ? 'none' : `1px solid ${T.rule}`,
                textDecoration: 'none',
                cursor: step.done || !step.to ? 'default' : 'pointer',
                transition: 'opacity .12s',
                opacity: step.done ? 0.55 : 1,
              }}
              onMouseEnter={e => { if (!step.done && step.to) e.currentTarget.style.opacity = 0.75 }}
              onMouseLeave={e => { if (!step.done && step.to) e.currentTarget.style.opacity = 1 }}
            >
              {/* Pastille check */}
              <div style={{
                width: 22, height: 22, flexShrink: 0,
                background: step.done ? T.gold : 'transparent',
                border: `1.5px solid ${step.done ? T.gold : T.rule}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '50%',
              }}>
                {step.done && <Check size={12} color={T.dark} strokeWidth={3} />}
              </div>

              {/* Texte */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: T.mono, fontSize: 11, color: T.ink, fontWeight: 700,
                  textDecoration: step.done ? 'line-through' : 'none',
                  letterSpacing: '.02em',
                }}>
                  {step.label}
                </div>
                {step.hint && !step.done && (
                  <div style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, marginTop: 2, letterSpacing: '.02em' }}>
                    {step.hint}
                  </div>
                )}
              </div>

              {/* Chevron */}
              {!step.done && step.to && (
                <ChevronRight size={14} color={T.gold} style={{ flexShrink: 0 }} />
              )}
            </Wrapper>
          )
        })}
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────

export default function DashboardHome() {
  const { user, refreshUser } = useAuth()
  const { selectedSeason, currentSeason, nextSeason, isCurrentSeason, isNextSeason, isPastSeason, isSeasonPrepared } = useSeason()
  const [matches, setMatches]             = useState([])
  const [players, setPlayers]             = useState([])
  const [quotaData, setQuotaData]         = useState(null)
  const [creditsData, setCreditsData]     = useState(null)
  const [evoMatches, setEvoMatches]       = useState([])
  const [loading, setLoading]             = useState(true)
  const [isMobile, setIsMobile]           = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [identityCompleted, setIdentityCompleted] = useState(false)

  const [assistantOpen, setAssistantOpen] = useState(false)

  // Saison à venir non préparée → on affiche un écran d'invitation à l'assistant.
  // Réservé aux plans payants : les FREE ont 1 match max sur 30j, ils n'ont
  // pas de saison à préparer.
  // A11 · la préparation de saison était masquée aux membres de club.
  const isFreePlan = estVraiFree(user)
  const showSeasonPrep = isNextSeason && !isSeasonPrepared(selectedSeason) && !isFreePlan

  // Détection DS pur (plan CLUB/CLUB_PRO sans catégorie gérée → gestionnaire uniquement)
  const isDSPur = !!(user && (user.plan === 'CLUB' || user.plan === 'CLUB_PRO') && user?.role === 'ADMIN' && !user?.managed_category)

  // Vérification identité coach à afficher ou non
  const isClubAdmin = !!(user?.club_id && (user?.plan === 'CLUB' || user?.plan === 'CLUB_PRO') && user?.role === 'ADMIN')

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // BUG-027 R6 — safety net cache stale. Un user rattaché à un club
  // (club_id présent) mais dont club_name manque dans le cache localStorage
  // affichait le fallback "Mon équipe". Cas typique : cache hydraté avant
  // que /auth/me ne renvoie club_name. Condition stricte (club_id && pas de
  // club_name) pour ne pas re-fetch /auth/me à chaque visite du dashboard.
  useEffect(() => {
    if (user?.club_id && !user?.club_name) {
      refreshUser()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    ;(async () => {
      try {
        const [m, p, q, evo, cr] = await Promise.all([
          matchService.getMatches({ limit: 4 }),
          playerService.getPlayers(),
          api.get('/matches/quota'),
          matchService.getMatches({ limit: 8, status: 'COMPLETED' }),
          api.get('/matches/credits/status').catch(() => ({ data: null })),
        ])
        setMatches(m); setPlayers(p); setQuotaData(q.data); setCreditsData(cr.data)

        const sorted = [...evo]
          .filter(m => !['AMICAL', 'amical'].includes(m.type))
          .sort((a, b) => new Date(a.date) - new Date(b.date))
        setEvoMatches(sorted)
      } catch (e) { console.error(e) }
      finally { setLoading(false) }
    })()
  }, [])

  // Vérifier si la checklist d'onboarding doit s'afficher
  // - DS Admin de club : pas concerné (parcours différent)
  // - Si déjà complétée (champ User.onboarding_completed) : ne réapparaît jamais
  // - Sinon : on regarde l'état de l'identité coach + on laisse les states matches/players faire le reste
  useEffect(() => {
    if (isClubAdmin) return
    if (user?.onboarding_completed) return

    const checkIdentity = async () => {
      try {
        const { data } = await api.get('/account/coach-identity/')
        setIdentityCompleted(!!data.completed)
      } catch {
        setIdentityCompleted(false)
      } finally {
        setShowOnboarding(true)
      }
    }
    checkIdentity()
  }, [isClubAdmin, user?.onboarding_completed])

  // Construire les étapes
  const onboardingSteps = [
    {
      key: 'account',
      label: 'Créer ton compte',
      done: true,
      to: null,
    },
    {
      key: 'profile',
      label: 'Définir ton identité de jeu',
      hint: 'Vision, posture, style. Personnalise tes recommandations.',
      done: identityCompleted,
      to: '/dashboard/settings',
      state: { openIdentity: true },
    },
    {
      key: 'players',
      label: 'Ajouter ton effectif',
      hint: 'Au moins un joueur pour commencer.',
      done: players.length > 0,
      to: '/dashboard/players',
    },
    {
      key: 'firstMatch',
      label: 'Saisir ton premier match',
      hint: 'Score, événements, ressenti — la vidéo est optionnelle.',
      done: matches.length > 0,
      to: '/dashboard/matches/new',
    },
  ]

  const allOnboardingDone = onboardingSteps.every(s => s.done)

  // Une fois les 4 étapes cochées, on persiste en backend pour ne plus jamais réafficher
  useEffect(() => {
    if (allOnboardingDone && showOnboarding && !loading && !user?.onboarding_completed) {
      api.patch('/account/onboarding-completed/', {})
        .then(() => {
          // Le backend a bien enregistré, plus besoin d'afficher la checklist
          setShowOnboarding(false)
        })
        .catch(err => console.error('Failed to mark onboarding complete:', err))
    }
  }, [allOnboardingDone, showOnboarding, loading, user?.onboarding_completed])

  const shouldDisplayOnboarding = showOnboarding && !allOnboardingDone && !loading


  const quota      = quotaData?.quota     ?? null
  const quotaUsed  = quotaData?.used      ?? null
  const quotaLeft  = quotaData?.remaining ?? null
  const quotaPct   = quota ? Math.min((quotaUsed / quota) * 100, 100) : 0
  const quotaColor = quotaPct >= 100 ? T.red : quotaPct >= 75 ? T.orange : T.gold

  const completed     = matches.filter(m => m.status === 'completed' || m.status === 'COMPLETED').length
  const processing    = matches.filter(m => m.status === 'processing' || m.status === 'PROCESSING').length
  const recentMatches = matches.slice(0, 4)
  const topPlayers    = players.slice(0, 5)

  // A12 · CETTE LIGNE testait le PLAN la ou les trois autres copies du
  // detecteur testent `managed_category`. Un membre de club porte un plan
  // personnel FREE : la variante le manquait, et ses graphiques etaient
  // calcules sur `userPlan = 'FREE'`.
  const isClubMember = estMembreDeClub(user)
  const userPlan     = isClubMember ? 'CLUB' : (user?.plan || 'COACH').toUpperCase()

  const evoSeries = {
    possession:    evoMatches.map(m => normStats(m.stats).possession).filter(v => v !== null),
    passAccuracy:  evoMatches.map(m => normStats(m.stats).passAccuracy).filter(v => v !== null),
    shotsOnTarget: evoMatches.map(m => normStats(m.stats).shotsOnTarget).filter(v => v !== null),
  }
  const hasEvo = Object.values(evoSeries).some(s => s.length >= 2)

  // ── Hero : fontSize adaptatif selon longueur du nom ──
  const clubName  = user?.club_name || 'Mon équipe'
  const firstName = user?.name?.split(' ')[0] || 'Coach'
  const maxLen    = Math.max(clubName.length, `Bonjour, ${firstName}.`.length)
  const heroFontSize = isMobile ? 22 : maxLen > 22 ? 26 : maxLen > 16 ? 32 : maxLen > 12 ? 36 : 42

  const SkeletonRow = ({ h = 56 }) => (
    <div style={{ height: h, background: `linear-gradient(90deg, ${T.rule} 25%, rgba(26,25,22,0.04) 50%, ${T.rule} 75%)`, backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
  )

  return (
    <DashboardLayout>
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
        .match-row:hover  { background: ${T.goldBg} !important; }
        .player-row:hover { background: ${T.goldBg} !important; }
        .action-row:hover { background: ${T.goldBg} !important; }
      `}</style>

      {/* Assistant de préparation saison */}
      <SeasonAssistant
        isOpen={assistantOpen}
        onClose={() => setAssistantOpen(false)}
        targetSeason={nextSeason}
      />

      {/* ── ÉCRAN PRÉPARATION SAISON À VENIR ── */}
      {showSeasonPrep && isDSPur && (
        <SeasonClubOverview targetSeason={nextSeason} isMobile={isMobile} />
      )}

      {showSeasonPrep && !isDSPur && (
        <div style={{
          position: 'relative', padding: isMobile ? '32px 20px' : '56px 48px',
          marginBottom: 24, overflow: 'hidden',
          animation: 'fadeUp .4s ease both', textAlign: 'center',
          backgroundImage: 'url(https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=1400&q=80&fit=crop)',
          backgroundSize: 'cover', backgroundPosition: 'center 60%',
        }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,9,8,0.72)', pointerEvents: 'none' }} />
          {/* Grille déco */}
          <div style={{
            position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.04,
            backgroundImage: 'linear-gradient(rgba(79,142,247,1) 1px, transparent 1px), linear-gradient(90deg, rgba(79,142,247,1) 1px, transparent 1px)',
            backgroundSize: '36px 36px',
          }} />

          <div style={{ position: 'relative', maxWidth: 560, margin: '0 auto' }}>
            <div style={{
              width: 64, height: 64, margin: '0 auto 24px',
              background: 'rgba(79,142,247,0.10)', border: `1.5px solid ${T.gold}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              animation: 'pulse 2.5s ease-in-out infinite',
            }}>
              <Sparkles size={26} color={T.gold} />
            </div>

            <p style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(79,142,247,0.85)', marginBottom: 16 }}>
              Saison {nextSeason} · À préparer
            </p>

            <h1 style={{ fontFamily: T.display, fontSize: isMobile ? 32 : 48, textTransform: 'uppercase', lineHeight: 0.95, letterSpacing: '.01em', color: '#f5f2eb', marginBottom: 18 }}>
              Prépare<br />
              <span style={{ color: T.gold }}>ta nouvelle saison.</span>
            </h1>

            <p style={{ fontFamily: T.body, fontSize: 15, color: 'rgba(245,242,235,0.65)', lineHeight: 1.6, marginBottom: 32, maxWidth: 460, margin: '0 auto 32px' }}>
              Définis ton club, ta catégorie et ton effectif pour la saison {nextSeason} en moins de 2 minutes.
            </p>

            <button
              onClick={() => setAssistantOpen(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                padding: '16px 32px',
                background: T.gold, color: T.dark,
                border: 'none', cursor: 'pointer',
                fontFamily: T.mono, fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', fontWeight: 700,
                transition: 'transform .15s, box-shadow .15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(79,142,247,0.3)' }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none' }}
            >
              Préparer ma saison
              <ArrowRight size={14} />
            </button>

            <p style={{ marginTop: 20, fontFamily: T.mono, fontSize: 9, color: 'rgba(245,242,235,0.4)', letterSpacing: '.1em' }}>
              Tu pourras tout modifier après.
            </p>
          </div>
        </div>
      )}

      {/* Si on est sur la saison à venir non préparée et qu'on est un coach individuel, on s'arrête ici */}
      {showSeasonPrep && !isDSPur && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: T.muted, fontFamily: T.mono, fontSize: 11, letterSpacing: '.06em' }}>
          Le tableau de bord se remplira au fil de la saison {nextSeason}.
        </div>
      )}

      {/* Le reste du dashboard ne s'affiche QUE si on n'est pas en mode préparation */}
      {!showSeasonPrep && (<>

      {/* ── HERO ── */}
      <div style={{
        position: 'relative', padding: isMobile ? '20px 18px' : '28px 32px',
        marginBottom: 24, overflow: 'hidden',
        animation: 'fadeUp .4s ease both',
        backgroundImage: 'url(https://images.unsplash.com/photo-1529900748604-07564a03e7a6?w=1200&q=80&fit=crop)',
        backgroundSize: 'cover',
        backgroundPosition: 'center 30%',
      }}>
        {/* Overlay sombre pour lisibilité du texte */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'linear-gradient(135deg, rgba(10,9,8,0.88) 0%, rgba(10,9,8,0.72) 60%, rgba(10,9,8,0.82) 100%)',
        }} />
        {/* Grille déco subtile */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.03,
          backgroundImage: 'linear-gradient(rgba(79,142,247,1) 1px, transparent 1px), linear-gradient(90deg, rgba(79,142,247,1) 1px, transparent 1px)',
          backgroundSize: '36px 36px',
        }} />

        <div style={{ position: 'relative', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'flex-end', justifyContent: 'space-between', gap: isMobile ? 14 : 20 }}>

          {/* Gauche : logo + texte */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, minWidth: 0, flex: 1 }}>
            {/* k13 : logo presigned (bucket privé). Pas de fallback texte
                ici : le <h1> ci-dessous porte déjà {clubName} (design
                conservé) — éviter le doublon + plus d'image cassée. */}
            {user?.club_name && (
              <div style={{ flexShrink: 0, marginBottom: 4, opacity: 0.9 }}>
                <ClubLogoOrName size="lg" name={user.club_name} textFallback={false} />
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <p style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.18em', textTransform: 'uppercase', color: 'rgba(79,142,247,0.9)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 14, height: 1, background: T.gold, display: 'inline-block' }} />
                Tableau de bord · Saison {formatSeasonShort(selectedSeason)}
              </p>
              <h1 style={{ fontFamily: T.display, fontSize: isMobile ? 28 : 40, fontWeight: 900, textTransform: 'uppercase', lineHeight: .9, letterSpacing: '-.01em', color: '#FFFFFF' }}>
                {clubName}<br />
                <span style={{ color: T.gold, fontSize: isMobile ? 22 : Math.round((isMobile ? 28 : 40) * 0.7), fontWeight: 800 }}>Bonjour, {firstName}.</span>
              </h1>
            </div>
          </div>

          {/* Crédits analyse — masque si plan FREE ou aucun credit (non-sens
              produit + regle copy IA hors Coach+) */}
          {creditsData !== null && !isFreePlan && (
            (() => {
              const dispo = creditsData?.analyses_disponibles ?? creditsData?.analysis_credits ?? 0
              const quota = creditsData?.analyses_quota ?? 0
              if (dispo <= 0) return null
              return (
                <div style={{ textAlign: 'left', flexShrink: 0 }}>
                  <p style={{ fontFamily: T.mono, fontSize: 8, fontWeight: 600, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>Analyses IA</p>
                  <p style={{ fontFamily: T.display, fontSize: isMobile ? 28 : 36, fontWeight: 800, lineHeight: 1, color: T.gold }}>
                    {dispo}
                    {quota > 0 && <span style={{ fontFamily: T.mono, fontSize: 14, fontWeight: 400, color: 'rgba(255,255,255,0.3)' }}>/{quota}</span>}
                    <span style={{ fontFamily: T.mono, fontSize: 10, fontWeight: 400, color: 'rgba(255,255,255,0.4)', marginLeft: 4 }}>disponibles</span>
                  </p>
                  {quota > 0 && (
                    <div style={{ marginTop: 8, height: 3, width: '100%', maxWidth: 140, background: 'rgba(255,255,255,0.08)' }}>
                      <div style={{ height: '100%', width: `${Math.min(dispo / quota * 100, 100)}%`, background: T.gold, transition: 'width .4s ease' }} />
                    </div>
                  )}
                </div>
              )
            })()
          )}
        </div>
      </div>

      {/* Bandeau essai Free — limite a DashboardHome (auto-masque si plan paye) */}
      <FreeExpiryBanner />

      {/* ── CHECKLIST D'ONBOARDING ── */}
      {shouldDisplayOnboarding && (
        <OnboardingChecklist steps={onboardingSteps} />
      )}

      {/* ── KPI STRIP ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 8, marginBottom: 24 }}>
        <KpiCard label="Matchs analysés"  value={completed}      sub="Ce mois"             accentIdx={0} delay={0}  />
        <KpiCard label="En cours"         value={processing}     sub="En analyse"           accentIdx={1} delay={40} />
        <KpiCard label="Joueurs effectif" value={players.length} sub="Enregistrés"          accentIdx={2} delay={80} />
        <KpiCard label="Analyses IA"  value={creditsData?.analyses_disponibles ?? creditsData?.analysis_credits ?? '—'}  sub="Disponibles ce mois" accentIdx={3} delay={120} />
      </div>

      {/* ── ÉVOLUTION ── */}
      {hasEvo && (
        <div style={{ marginBottom: 24, animation: 'fadeUp .4s ease 100ms both' }}>
          <div style={{ background: T.surface, border: `0.5px solid ${T.rule}`, marginBottom: 8 }}>
            <SectionHeader title="Évolution" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: 8 }}>
            <EvoCard label="Possession"      values={evoSeries.possession}    unit="%" decimals={0} delay={0}  />
            <EvoCard label="Passes réussies" values={evoSeries.passAccuracy}  unit="%" decimals={0} delay={40} />
            {/* PROMESSES-ANALYSE-001 · carte « Tirs cadrés » retiree. Elle
                etait DEJA invisible — `EvoCard` rend `null` sous deux valeurs,
                et `shots` est absent des `match.stats` des 7 matchs analyses.
                Retiree quand meme · un libelle qui promet une mesure jamais
                produite finit par etre remis en service par quelqu'un qui le
                croit casse. */}
          </div>
        </div>
      )}

      {/* ── CONTENU 2 COLONNES ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 340px', gap: 8, alignItems: 'start' }}>

        {/* COL GAUCHE */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, order: isMobile ? 2 : 1 }}>

          {/* Derniers matchs */}
          <div style={{ background: T.surface, border: `0.5px solid ${T.rule}`, animation: 'fadeUp .4s ease 80ms both' }}>
            <SectionHeader title="Derniers matchs" to="/dashboard/matches" />
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1, padding: '1px 0' }}>
                {[1,2,3].map(i => <SkeletonRow key={i} />)}
              </div>
            ) : recentMatches.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                <Film size={28} color={T.goldBdr} style={{ marginBottom: 12 }} />
                <p style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, letterSpacing: '.06em' }}>Aucun match analysé</p>
                <Link to="/dashboard/matches/new" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 16, padding: '10px 20px', background: T.gold, color: T.dark, fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700, textDecoration: 'none' }}>
                  <Upload size={11} /> Nouveau match
                </Link>
              </div>
            ) : recentMatches.map((m, i) => (
              <Link key={m.id} to={`/dashboard/matches/${m.id}`} className="match-row" style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '12px 20px',
                borderBottom: i < recentMatches.length - 1 ? `0.5px solid ${T.rule}` : 'none',
                textDecoration: 'none', background: T.surface, transition: 'background .12s',
              }}>
                <div style={{ width: 36, textAlign: 'center', flexShrink: 0, paddingLeft: 8, borderLeft: `2px solid ${T.rule}` }}>
                  <div style={{ fontFamily: T.display, fontSize: 22, fontWeight: 700, lineHeight: 1, color: T.ink }}>{new Date(m.date).getDate()}</div>
                  <div style={{ fontFamily: T.mono, fontSize: 8, fontWeight: 500, textTransform: 'uppercase', color: T.muted, marginTop: 2 }}>
                    {new Date(m.date).toLocaleDateString('fr-FR', { month: 'short' })}
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: T.display, fontSize: 16, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.02em', color: T.ink }}>vs {m.opponent}</span>
                    {m.category && (
                      <span style={{ fontFamily: T.mono, fontSize: 8, fontWeight: 500, padding: '2px 6px', border: `0.5px solid ${T.rule}`, color: T.muted, borderRadius: T.radius }}>{m.category}</span>
                    )}
                  </div>
                  {/* Oriente club : le libelle dit « vs {adversaire} » juste
                      au-dessus, donc notre score passe en premier. Compare
                      brut, score_home donnait « Victoire » sur une defaite a
                      l'exterieur (cf. src/utils/matchResult.js). */}
                  {hasScore(m) ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontFamily: T.display, fontSize: 22, fontWeight: 700, lineHeight: 1, color: T.ink }}>{scoreDisplay(m, ' – ')}</span>
                      <span style={{
                        fontFamily: T.mono, fontSize: 8, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', padding: '2px 8px',
                        color: isWin(m) ? T.green : isDefeat(m) ? T.red : T.orange,
                        background: isWin(m) ? T.greenBg : isDefeat(m) ? T.redBg : T.orangeBg,
                        border: `1px solid ${isWin(m) ? T.greenBdr : isDefeat(m) ? T.redBdr : T.orangeBdr}`,
                        borderRadius: 20,
                      }}>
                        {resultLabel(m)}
                      </span>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <StatusBadge status={m.status} />
                      {m.status === 'processing' && m.progress > 0 && (
                        <div style={{ flex: 1, maxWidth: 120, height: 2, background: T.rule, borderRadius: 1 }}>
                          <div style={{ height: '100%', width: `${m.progress}%`, background: T.blue, borderRadius: 1 }} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <ChevronRight size={13} color={T.muted} style={{ flexShrink: 0 }} />
              </Link>
            ))}
          </div>

          {/* Effectif */}
          <div style={{ background: T.surface, border: `0.5px solid ${T.rule}`, animation: 'fadeUp .4s ease 140ms both' }}>
            <SectionHeader title="Effectif" to="/dashboard/players" />
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[1,2,3,4].map(i => <SkeletonRow key={i} h={48} />)}
              </div>
            ) : topPlayers.length === 0 ? (
              <p style={{ padding: '28px 20px', fontFamily: T.mono, fontSize: 11, color: T.muted, textAlign: 'center', letterSpacing: '.06em' }}>
                Aucun joueur enregistré
              </p>
            ) : topPlayers.map((p, i) => (
              <div key={p.id} className="player-row" style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px',
                borderBottom: i < topPlayers.length - 1 ? `0.5px solid ${T.rule}` : 'none',
                background: T.surface, transition: 'background .12s',
              }}>
                <span style={{ fontFamily: T.display, fontSize: 14, fontWeight: 700, color: T.muted2, width: 18, textAlign: 'center', flexShrink: 0 }}>{i + 1}</span>
                {p.photo_url
                  ? <img src={p.photo_url} alt={p.name} style={{ width: 32, height: 32, objectFit: 'cover', flexShrink: 0, borderRadius: '50%', border: `1px solid ${T.rule}` }} />
                  : <div style={{ width: 32, height: 32, borderRadius: '50%', background: T.bgAlt, border: `1px solid ${T.rule}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontFamily: T.display, fontSize: 12, fontWeight: 700, color: T.ink2 }}>{p.name?.[0]}</span>
                    </div>
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: T.mono, fontSize: 12, fontWeight: 600, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                  <div style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 500, textTransform: 'uppercase', color: T.muted, marginTop: 1 }}>{p.position}</div>
                </div>
                {p.number && <span style={{ fontFamily: T.display, fontSize: 16, fontWeight: 700, color: T.muted }}>{p.number}</span>}
              </div>
            ))}
          </div>
        </div>

        {/* COL DROITE */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, order: isMobile ? 1 : 2 }}>

          {/* CTA Upload */}
          <div style={{ background: T.dark, padding: '22px 20px', position: 'relative', overflow: 'hidden', animation: 'fadeUp .4s ease 60ms both', borderRadius: T.radius }}>
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <Zap size={13} color={T.gold} />
                <span style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(79,142,247,0.85)' }}>Nouveau match</span>
              </div>
              <div style={{ fontFamily: T.display, fontSize: 32, fontWeight: 800, textTransform: 'uppercase', color: '#FFFFFF', lineHeight: .9, marginBottom: 10 }}>
                Saisir<br /><span style={{ color: T.gold }}>un match.</span>
              </div>
              <p style={{ fontFamily: T.mono, fontSize: 11, fontWeight: 400, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, marginBottom: 16 }}>
                Score, événements, ressenti. La vidéo est optionnelle.
              </p>
              <Link to="/dashboard/matches/new" style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px',
                background: '#FFFFFF', color: T.dark, fontFamily: T.mono, fontSize: 11, fontWeight: 600,
                borderRadius: T.radius,
                textDecoration: 'none', transition: 'opacity .12s', minHeight: 44,
              }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                <Upload size={12} /> Nouveau match
              </Link>
            </div>
          </div>

          {/* Activité */}
          <div style={{ background: T.surface, border: `0.5px solid ${T.rule}`, animation: 'fadeUp .4s ease 120ms both' }}>
            <SectionHeader title="Activité" />
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[1, 2].map(i => <SkeletonRow key={i} h={52} />)}
              </div>
            ) : (
              <div>
                <div className="action-row" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderBottom: `1px solid ${T.rule}`, background: T.surface, transition: 'background .12s' }}>
                  <div style={{ width: 28, height: 28, background: quotaColor + '14', border: `1px solid ${quotaColor}28`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Activity size={11} color={quotaColor} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontFamily: T.mono, fontSize: 10, color: T.ink, lineHeight: 1.4 }}>{quota !== null ? `${quotaUsed}/${quota} matchs utilisés ce mois` : '—'}</p>
                    <div style={{ marginTop: 5, height: 2, background: T.rule }}>
                      <div style={{ height: '100%', width: `${quotaPct}%`, background: quotaColor, transition: 'width .4s' }} />
                    </div>
                  </div>
                </div>
                {completed > 0 && (
                  <div className="action-row" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderBottom: `1px solid ${T.rule}`, background: T.surface, transition: 'background .12s' }}>
                    <div style={{ width: 28, height: 28, background: T.greenBg, border: `1px solid ${T.greenBdr}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <CheckCircle size={11} color={T.green} />
                    </div>
                    <p style={{ fontFamily: T.mono, fontSize: 10, color: T.ink, lineHeight: 1.4 }}>
                      {completed} match{completed > 1 ? 's' : ''} analysé{completed > 1 ? 's' : ''} ce mois
                    </p>
                  </div>
                )}
                {processing > 0 && (
                  <div className="action-row" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', background: T.surface, transition: 'background .12s' }}>
                    <div style={{ width: 28, height: 28, background: T.blueBg, border: `1px solid ${T.blueBdr}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Clock size={11} color={T.blue} />
                    </div>
                    <p style={{ fontFamily: T.mono, fontSize: 10, color: T.ink, lineHeight: 1.4 }}>
                      {processing} analyse{processing > 1 ? 's' : ''} en cours
                    </p>
                  </div>
                )}
                {completed === 0 && processing === 0 && (
                  <p style={{ padding: '20px', fontFamily: T.mono, fontSize: 10, color: T.muted, letterSpacing: '.04em' }}>Aucune activité récente</p>
                )}
              </div>
            )}
          </div>

          {/* PROMESSES-ANALYSE-001 · le lien « Vue Club » a ete retire avec
              la page · elle affichait TROIS EQUIPES INVENTEES (`MOCK_TEAMS`)
              a un directeur sportif, presentees comme les siennes · scores,
              formes W-D-L, possession, pressing, sprints. Aucun appel API,
              aucune donnee reelle. Une accroche trop optimiste decoit ; un
              faux chiffre presente comme reel fait perdre la confiance. */}
        </div>
      </div>
      </>)}
    </DashboardLayout>
  )
}
